// ─── FICHE d'une opération réelle (lecture) ───────────────────────
// Le principe app-wide veut que le TAP informe et que le swipe → modifie
// (AGENTS.md). Le relevé de compte l'enfreignait depuis 0.6.4.0 : taper une
// opération ouvrait directement son édition. Signalé par Cédric le 10/08.
//
// C'est aussi l'écran qui montre le rattachement DANS L'AUTRE SENS : depuis la
// prévision on voit les opérations qui l'honorent (FicheOccurrence) ; ici, on
// voit ce que cette opération honore, et pour quelle part.
import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { Feuille, CalqueChoix, LigneSwipe, useUI, useT, frJMA, HIT } from '@mop/core';
import { formatEuro, montantOperation, dateEffective } from './services/calculs';
import { emojiCategorie, cleCategorie, clePaiement } from './data/categories';
import {
  rattachementsDeDepense, repartitionDepense, pourcentage, EPSILON,
} from './services/repartition';

export default function FicheOperation({
  visible, operation, rattachements = [], lignes = [], budgets = [],
  nomCompte = '', nomBudget = '',
  onFermer, onEditer, onDetacher, onRattacher,
  // Modifier la PART d'un rattachement existant (Cédric, 16/08) : le swipe →
  // ouvre l'édition € / %, le montant fait foi.
  onModifierPart,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  // ⚠️ Tous les hooks AVANT le moindre retour conditionnel (règle React).
  const o = operation || {};
  // La part en cours d'édition : { r, saisie, pct } — `pct` tel que tapé,
  // sinon « 33, » serait réécrit entre deux frappes.
  // ⚠️ `pct: null` = jamais touché (on affiche le calculé) ; `''` = VIDÉ par
  // l'utilisateur (on affiche vide). Confondre les deux rendait le champ
  // impossible à effacer : le calculé renaissait à chaque suppression
  // (constaté par Cédric le 16/08).
  const [partEdit, setPartEdit] = useState(null);
  const parts = useMemo(
    () => rattachementsDeDepense(rattachements, o.id).map(r => ({
      ...r, ligne: lignes.find(l => l.id === r.ligneId) || null,
    })),
    [rattachements, lignes, o.id],
  );
  const rep = useMemo(() => repartitionDepense(o, rattachements), [o, rattachements]);

  // Les budgets atteints INDIRECTEMENT, par les previsions que l'operation
  // honore. Dedoublonnes : deux parts dans la meme enveloppe ne font pas
  // deux budgets.
  // Les LIGNES honorées, pour la rangée « Ligne » (Cédric, 15/08) : le
  // budget seul ne dit pas QUELLE prévision l'opération honore dedans.
  const lignesIndirectes = useMemo(
    () => [...new Set(parts.map(r => r.ligne?.libelle).filter(Boolean))],
    [parts],
  );
  const budgetsIndirects = useMemo(() => {
    const noms = new Set();
    for (const r of parts) {
      const b = budgets.find(x => x.id === r.ligne?.budgetId);
      if (b?.nom) noms.add(b.nom);
    }
    return [...noms];
  }, [parts, budgets]);

  if (!visible || !operation) return null;
  const revenu = o.sens === 'revenu';

  return (
    <Feuille visible={visible} titre={o.libelle || t(cleCategorie(o.categorie))}
      emoji={emojiCategorie(o.categorie)} hauteur={0.85} onFermer={onFermer}
      action={{ icone: '✏️', label: t('commun.modifier'), onPress: onEditer }}>

      <View style={s.bandeau}>
        <Text style={[s.montant, revenu && { color: UI.ok || '#2e7d32' }]}>
          {montantOperation(o.montant, o.sens)}
        </Text>
        <Text style={s.date}>{frJMA(dateEffective(o))}</Text>
      </View>

      {/* Les deux dimensions que porte toute opération : d'OÙ vient l'argent
          (le compte) et À QUOI il sert (l'enveloppe). Cf. CONCEPTION-FINANCE. */}
      <View style={s.infos}>
        {!!nomCompte && <Ligne s={s} lbl={t('finances.comptes')} val={`🏦 ${nomCompte}`} />}
        {/* Le RANGEMENT direct de l'operation (son `budgetId`) et sa
            JUSTIFICATION (ce qu'elle honore) sont deux choses distinctes —
            Cedric l'a rappele le 11/08. Une operation importee de la banque
            n'a pas de rangement : elle affichait donc « — » alors qu'elle
            honore bel et bien une prevision d'un budget (14/08).
            On montre alors le budget DEDUIT, en disant qu'il est indirect. */}
        <Ligne s={s} lbl={t('finances.budget')}
          val={nomBudget
            ? `💰 ${nomBudget}`
            : (budgetsIndirects.length
              ? `💰 ${budgetsIndirects.join('  ·  ')}  (${t('finances.viaPrevision')})`
              : '—')} />
        {/* La ou les lignes de budget honorées, sous le budget (15/08). */}
        {lignesIndirectes.length > 0 && (
          <Ligne s={s} lbl={t('finances.ligneBudget')}
            val={lignesIndirectes.join('  ·  ')} />
        )}
        <Ligne s={s} lbl={t('finances.categorie')} val={t(cleCategorie(o.categorie))} />
        {!!o.typePaiement && <Ligne s={s} lbl={t('budget.typePaiement')} val={t(clePaiement(o.typePaiement))} />}
        {o.paye === false && <Ligne s={s} lbl={t('finances.st_avenir')} val="⏳" />}
        {!!o.exclu && <Ligne s={s} lbl={t('finances.horsCalcul')} val="⊘" />}
        {!!o.libelleBanque && o.libelleBanque !== o.libelle && (
          <Ligne s={s} lbl={t('finances.reference')} val={o.libelleBanque} />
        )}
      </View>

      {!!o.commentaire && <Text style={s.commentaire}>{o.commentaire}</Text>}

      {/* ── Ce que cette opération honore ─────────────────────── */}
      {/* Le ＋ à droite du titre (Cédric, 16/08) : ajouter une ligne — dès le
          PREMIER rattachement — ouvre la sélection multi-lignes en arbre. */}
      <View style={s.titreRangee}>
        <Text style={s.titre}>{t('finances.honore')}</Text>
        {!!onRattacher && (
          <TouchableOpacity onPress={() => onRattacher(o)} hitSlop={HIT}
            accessibilityLabel={t('finances.actRattacher')}>
            <Text style={s.titrePlus}>＋</Text>
          </TouchableOpacity>
        )}
      </View>

      {parts.length === 0 ? (
        <Text style={s.vide}>{t('finances.aucuneOpRattachee')}</Text>
      ) : parts.map(r => (
        /* Gestes standard (16/08) : swipe ← détacher, swipe → modifier la
           part — le ⊝ inline disparaît, le tap reste à l'information. */
        <LigneSwipe key={r.id} espacement={8} enabled
          onSupprimer={onDetacher ? () => onDetacher(r) : undefined}
          onEditer={onModifierPart
            ? () => setPartEdit({ r, saisie: String(r.montant ?? ''), pct: null })
            : undefined}>
          <View style={s.part}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={s.partLib} numberOfLines={1}>
                {r.ligne?.libelle || t('finances.aRattacher')}
              </Text>
              {/* Le POURCENTAGE en plus du montant (Cedric, 14/08) : « 84 € »
                  ne dit pas si l'operation est presque entierement affectee a
                  cette prevision ou a peine entamee. */}
              <Text style={s.partSous}>
                {r.mois}  ·  {pourcentage(r.montant, o.montant)} %
              </Text>
            </View>
            <Text style={s.partMontant}>{formatEuro(r.montant)}</Text>
          </View>
        </LigneSwipe>
      ))}

      {/* Le RESTE est la part de l'opération qui n'honore aucune prévision.
          Affiché à zéro, ce serait du bruit permanent : on ne le montre que
          s'il reste effectivement quelque chose à affecter. */}
      {rep.reste > EPSILON && (
        <TouchableOpacity style={s.reste} activeOpacity={0.7} onPress={() => onRattacher?.(o)}>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={s.resteLbl}>{t('finances.nonPrevu')}</Text>
            <Text style={s.resteAide}>{t('finances.actRattacher')}</Text>
          </View>
          <Text style={s.resteVal}>{formatEuro(rep.reste)}</Text>
        </TouchableOpacity>
      )}

      {/* ÉDITION d'une part : CALQUE et non <Feuille> — on est déjà dans une
          Feuille. Montant et % liés, le montant fait foi (règle maison). */}
      <CalqueChoix visible={!!partEdit} emoji="✏️"
        titre={partEdit?.r?.ligne?.libelle || t('finances.montantPrevu')}
        onFermer={() => setPartEdit(null)}>
        {(() => {
          if (!partEdit) return null;
          const totalOp = Math.abs(o.montant || 0);
          const valeur = parseFloat(String(partEdit.saisie).replace(',', '.')) || 0;
          const pctCalc = totalOp > 0 ? Math.round((valeur / totalOp) * 1000) / 10 : 0;
          return (
            <>
              <View style={s.rangeeMontant}>
                <TextInput style={[s.inputPart, { flex: 1 }]} value={partEdit.saisie}
                  onChangeText={(v) => setPartEdit(p => ({ ...p, saisie: v.replace(/[^0-9.,]/g, ''), pct: null }))}
                  keyboardType="decimal-pad" placeholder="0,00" placeholderTextColor={UI.faint} />
                <View style={s.zonePct}>
                  <TextInput style={s.inputPct}
                    value={partEdit.pct !== null ? partEdit.pct : String(pctCalc)}
                    onChangeText={(v) => {
                      const propre = v.replace(/[^0-9.,]/g, '');
                      const n = parseFloat(propre.replace(',', '.'));
                      setPartEdit(p => ({ ...p, pct: propre,
                        saisie: (totalOp && Number.isFinite(n))
                          ? String(Math.round(totalOp * (n / 100) * 100) / 100) : p.saisie }));
                    }}
                    keyboardType="decimal-pad" />
                  <Text style={s.pctSigne}>%</Text>
                </View>
              </View>
              <TouchableOpacity style={[s.btnPart, valeur <= 0.005 && { opacity: 0.35 }]}
                disabled={valeur <= 0.005} activeOpacity={0.7}
                onPress={() => {
                  onModifierPart?.(partEdit.r, Math.round(valeur * 100) / 100);
                  setPartEdit(null);
                }}>
                <Text style={s.btnPartTxt}>✓ {t('commun.valider')}</Text>
              </TouchableOpacity>
            </>
          );
        })()}
      </CalqueChoix>
    </Feuille>
  );
}

const Ligne = ({ s, lbl, val }) => (
  <View style={s.infoLigne}>
    <Text style={s.infoLbl}>{lbl}</Text>
    <Text style={s.infoVal} numberOfLines={2}>{val}</Text>
  </View>
);

const makeStyles = (UI) => StyleSheet.create({
  bandeau: { alignItems: 'center', paddingVertical: 6, marginBottom: 8 },
  montant: { fontSize: 26, fontWeight: '800', color: UI.ink },
  date: { fontSize: 13, color: UI.muted, marginTop: 2 },
  infos: { backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border, paddingHorizontal: 12, paddingVertical: 4 },
  infoLigne: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, paddingVertical: 8 },
  infoLbl: { fontSize: 12.5, color: UI.faint, fontWeight: '700', width: 110 },
  infoVal: { flex: 1, fontSize: 13.5, color: UI.ink, fontWeight: '600' },
  commentaire: { fontSize: 13, color: UI.muted, lineHeight: 18, marginTop: 10, fontStyle: 'italic' },
  titre: { fontSize: 12, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3, marginTop: 18, marginBottom: 6 },
  titreRangee: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  titrePlus: { fontSize: 19, color: UI.accent, fontWeight: '700', marginTop: 10 },
  vide: { fontSize: 13, color: UI.faint, lineHeight: 18 },
  // Plus de marginBottom : l'espacement vient de LigneSwipe (espacement={8}),
  // une marge sur la carte ferait dépasser les boutons de swipe.
  part: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 9, paddingHorizontal: 12 },
  partLib: { fontSize: 14, fontWeight: '700', color: UI.ink },
  partSous: { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  partMontant: { fontSize: 14, fontWeight: '800', color: UI.ink },
  reste: { flexDirection: 'row', alignItems: 'center', gap: 10, borderRadius: 11, borderWidth: 1, borderColor: UI.accent, paddingVertical: 10, paddingHorizontal: 12, marginTop: 4 },
  resteLbl: { fontSize: 13.5, fontWeight: '800', color: UI.accent },
  resteAide: { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  resteVal: { fontSize: 15, fontWeight: '800', color: UI.accent },
  rangeeMontant: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  inputPart: { backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 11, paddingHorizontal: 12, fontSize: 16, fontWeight: '800', color: UI.ink },
  zonePct: { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingHorizontal: 12, paddingVertical: 11 },
  inputPct: { minWidth: 46, textAlign: 'right', fontSize: 16, fontWeight: '800', color: UI.ink },
  pctSigne: { fontSize: 15, fontWeight: '800', color: UI.muted },
  btnPart: { marginTop: 12, paddingVertical: 12, borderRadius: 11, backgroundColor: UI.accent, alignItems: 'center' },
  btnPartTxt: { fontSize: 14, color: '#fff', fontWeight: '800' },
});
